import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, Send, Server, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { adminApi } from '../../services/api';
import { supabase } from '../../supabaseClient';

const AdminEmailSettingsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [token, setToken] = useState(null);

  const [provider, setProvider] = useState('resend');

  // Resend fields
  const [resendApiKey, setResendApiKey] = useState('');
  const [resendSenderEmail, setResendSenderEmail] = useState('');

  // SMTP fields
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpSecurity, setSmtpSecurity] = useState('tls'); // 'none' | 'tls' | 'ssl'
  const [smtpSenderEmail, setSmtpSenderEmail] = useState('');

  const fetchConfig = useCallback(async (t) => {
    try {
      setLoading(true);
      const config = await adminApi.getEmailConfig(t);
      setProvider(config.provider || 'resend');
      setResendApiKey(config.resend_api_key || '');
      setResendSenderEmail(config.resend_sender_email || '');
      setSmtpHost(config.smtp_host || '');
      setSmtpPort(config.smtp_port || 587);
      setSmtpUsername(config.smtp_username || '');
      setSmtpPassword(config.smtp_password || '');
      if (config.smtp_use_ssl) setSmtpSecurity('ssl');
      else if (config.smtp_use_tls !== false) setSmtpSecurity('tls');
      else setSmtpSecurity('none');
      setSmtpSenderEmail(config.smtp_sender_email || '');
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    (async () => {
      const { data } = await supabase.auth.getSession();
      const t = data?.session?.access_token;
      if (!t) { navigate('/login'); return; }
      setToken(t);
      fetchConfig(t);
    })();
  }, [isAuthenticated, navigate, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        provider,
        resend_api_key: resendApiKey,
        resend_sender_email: resendSenderEmail,
        smtp_host: smtpHost,
        smtp_port: smtpPort,
        smtp_username: smtpUsername,
        smtp_password: smtpPassword,
        smtp_use_tls: smtpSecurity === 'tls',
        smtp_use_ssl: smtpSecurity === 'ssl',
        smtp_sender_email: smtpSenderEmail,
      };
      await adminApi.updateEmailConfig(payload, token);
      toast({ title: 'Saved', description: 'Email configuration updated successfully.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) return;
    setTesting(true);
    try {
      const result = await adminApi.testEmailConfig(testEmail, token);
      setTestDialogOpen(false);
      setTestEmail('');
      if (result.mock) {
        toast({ title: 'Not configured', description: 'Email service is in development mode. Configure credentials first.', variant: 'destructive' });
      } else if (result.success) {
        toast({ title: 'Test email sent', description: `Check ${testEmail} for the test email.` });
      } else {
        toast({ title: 'Failed', description: result.error || 'Could not send test email.', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin" size={40} style={{ color: 'var(--olive-primary)' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      <div className="container max-w-3xl py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon" style={{ borderRadius: 0 }}>
              <ArrowLeft size={16} />
            </Button>
          </Link>
          <div>
            <h1 className="heading-1">Email Settings</h1>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
              Choose your email provider and configure credentials
            </p>
          </div>
        </div>

        {/* Provider selector */}
        <div
          className="p-6 mb-6"
          style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
        >
          <h2 className="heading-3 mb-4">Email Provider</h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setProvider('resend')}
              className="p-4 text-left transition-colors"
              style={{
                border: provider === 'resend' ? '2px solid var(--olive-primary)' : '1px solid var(--border-medium)',
                backgroundColor: provider === 'resend' ? 'var(--bg-primary)' : 'transparent',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <Send size={20} style={{ color: provider === 'resend' ? 'var(--olive-primary)' : 'var(--text-secondary)' }} />
                <span className="heading-3">Resend</span>
              </div>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Cloud email API — simple setup with API key
              </p>
            </button>

            <button
              type="button"
              onClick={() => setProvider('smtp')}
              className="p-4 text-left transition-colors"
              style={{
                border: provider === 'smtp' ? '2px solid var(--olive-primary)' : '1px solid var(--border-medium)',
                backgroundColor: provider === 'smtp' ? 'var(--bg-primary)' : 'transparent',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <Server size={20} style={{ color: provider === 'smtp' ? 'var(--olive-primary)' : 'var(--text-secondary)' }} />
                <span className="heading-3">Custom SMTP</span>
              </div>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Use your own mail server (Gmail, Outlook, etc.)
              </p>
            </button>
          </div>
        </div>

        {/* Provider-specific fields */}
        <div
          className="p-6 mb-6"
          style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
        >
          {provider === 'resend' ? (
            <>
              <h2 className="heading-3 mb-4">Resend Configuration</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="resendApiKey">API Key</Label>
                  <Input
                    id="resendApiKey"
                    type="password"
                    value={resendApiKey}
                    onChange={(e) => setResendApiKey(e.target.value)}
                    placeholder="re_xxxxxxxxxx"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label htmlFor="resendSender">Sender Email</Label>
                  <Input
                    id="resendSender"
                    value={resendSenderEmail}
                    onChange={(e) => setResendSenderEmail(e.target.value)}
                    placeholder="OlivOak Villa <noreply@yourdomain.com>"
                    style={{ borderRadius: 0 }}
                  />
                  <p className="body-small mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Use format: Name &lt;email@domain.com&gt;
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <h2 className="heading-3 mb-4">SMTP Configuration</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="smtpHost">SMTP Host</Label>
                    <Input
                      id="smtpHost"
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                      placeholder="smtp.gmail.com"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtpPort">Port</Label>
                    <Input
                      id="smtpPort"
                      type="number"
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(Number(e.target.value))}
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="smtpUsername">Username</Label>
                  <Input
                    id="smtpUsername"
                    value={smtpUsername}
                    onChange={(e) => setSmtpUsername(e.target.value)}
                    placeholder="your-email@gmail.com"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label htmlFor="smtpPassword">Password / App Password</Label>
                  <Input
                    id="smtpPassword"
                    type="password"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label htmlFor="smtpSender">Sender Email</Label>
                  <Input
                    id="smtpSender"
                    value={smtpSenderEmail}
                    onChange={(e) => setSmtpSenderEmail(e.target.value)}
                    placeholder="reservations@olivoakvilla.com"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Connection Security</Label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="smtpSecurity" value="tls" checked={smtpSecurity === 'tls'} onChange={() => { setSmtpSecurity('tls'); setSmtpPort(587); }} />
                      <span className="body-small">STARTTLS (port 587)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="smtpSecurity" value="ssl" checked={smtpSecurity === 'ssl'} onChange={() => { setSmtpSecurity('ssl'); setSmtpPort(465); }} />
                      <span className="body-small">SSL/TLS (port 465)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="smtpSecurity" value="none" checked={smtpSecurity === 'none'} onChange={() => { setSmtpSecurity('none'); setSmtpPort(25); }} />
                      <span className="body-small">None (port 25)</span>
                    </label>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><Loader2 className="animate-spin mr-2" size={16} /> Saving...</> : 'Save Configuration'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setTestDialogOpen(true)}
            style={{ borderRadius: 0 }}
          >
            <Mail size={16} className="mr-2" /> Send Test Email
          </Button>
        </div>

        {/* Test Email Dialog */}
        <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Test Email</DialogTitle>
              <DialogDescription>
                Enter the email address where you'd like to receive the test email.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="testEmail">Recipient Email</Label>
              <Input
                id="testEmail"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                style={{ borderRadius: 0 }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleTest(); }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTestDialogOpen(false)} style={{ borderRadius: 0 }}>
                Cancel
              </Button>
              <Button className="btn-primary" onClick={handleTest} disabled={testing || !testEmail}>
                {testing ? <><Loader2 className="animate-spin mr-2" size={16} /> Sending...</> : 'Send'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminEmailSettingsPage;
